import { ThemeMode } from '../services/theme';

export interface PortfolioProject {
  title: string;
  description: string;
  tech: string[];
  demo: string;
  category: string;
}

export interface PortfolioKnowledge {
  profile: {
    name: string;
    fullName: string;
    role: string;
    tagline: string;
    summary: string;
    location: string;
    availability: string;
    experienceYears: string;
    projectsCompleted: string;
    happyClients: string;
    technologies: string;
  };
  skills: { name: string; desc: string }[];
  techStack: string[];
  services: { title: string; description: string; features: string[] }[];
  projects: PortfolioProject[];
  experiences: { title: string; company: string; period: string; description: string }[];
  certificates: { title: string; organization: string; type: string; period: string; location: string }[];
  recommendations: { name: string; title: string; text: string }[];
  education: string[];
  company: { name: string; about: string };
  contact: {
    location: string;
    phone: string;
    phoneRaw: string;
    email: string;
    whatsapp: string;
    workingHours: string;
  };
  socials: {
    github: string;
    linkedin: string;
    facebook: string;
    whatsapp: string;
    email: string;
    call: string;
  };
  resume: string;
  pages: { path: string; title: string; about: string }[];
}

/**
 * Single source of truth for the assistant's knowledge.
 * Data is collected from the existing Home, About, Services, Projects and
 * Contact pages so the AI never needs hardcoded replies.
 */
export const portfolioKnowledge: PortfolioKnowledge = {
  profile: {
    name: 'Sarfraz Ahmed',
    fullName: 'Sarfraz Ahmed Jamali',
    role: 'Full Stack Web Developer',
    tagline: 'Full-Stack Web Developer | MERN Stack Developer | Next.js Developer | PHP Developer | UI/UX Designer | Database Administrator',
    summary:
      'Building scalable, secure, and performant web applications with MERN Stack, PHP/MySQL, and modern cloud technologies. 3+ years of experience in full-stack development.',
    location: 'Pakistan',
    availability: 'Open to work / Available for Work',
    experienceYears: '3+',
    projectsCompleted: '25+',
    happyClients: '15+',
    technologies: '10+',
  },
  skills: [
    { name: 'Frontend Development', desc: 'React.js, Next.js, Tailwind CSS' },
    { name: 'Backend Development', desc: 'Node.js, Express.js, PHP' },
    { name: 'Database Design', desc: 'MongoDB, MySQL, Schema Design' },
    { name: 'Full Stack Architecture', desc: 'MERN Stack, PHP/MySQL' },
  ],
  techStack: [
    'HTML5',
    'CSS3',
    'JavaScript',
    'Bootstrap',
    'Tailwind CSS',
    'PHP',
    'Laravel',
    'MySQL',
    'React.js',
    'TypeScript',
    'Next.js',
    'Node.js',
    'Express.js',
    'MongoDB',
    'Git',
    'GitHub',
    'Postman',
    'GraphQL',
    'PostgreSQL',
    'Firebase',
    'Jest',
    'Webpack',
    'Docker',
    'AWS',
    'Figma',
  ],
  services: [
    {
      title: 'Frontend Development',
      description: 'Building responsive, interactive UIs with React.js, Tailwind CSS, and TypeScript',
      features: ['React.js', 'Tailwind CSS', 'TypeScript', 'HTML5/CSS3'],
    },
    {
      title: 'Backend Development',
      description: 'Creating scalable APIs with Node.js, Express.js, and PHP',
      features: ['Node.js', 'Express.js', 'PHP', 'REST APIs'],
    },
    {
      title: 'Database Design',
      description: 'Designing and optimizing both NoSQL and SQL databases',
      features: ['MongoDB', 'MySQL', 'Schema Design', 'Optimization'],
    },
    {
      title: 'PHP Development',
      description: 'Building robust web applications with PHP and Laravel',
      features: ['PHP', 'Laravel', 'Custom PHP', 'MySQL Integration'],
    },
    {
      title: 'Graphic & UI/UX Design',
      description: 'Creating beautiful, intuitive interfaces and stunning visuals',
      features: ['Canva Pro', 'Photoshop', 'Wireframing', 'Prototyping', 'User Testing'],
    },
    {
      title: 'Amazon Account Management',
      description: 'End-to-end Amazon FBA, FBM, and listing optimization',
      features: ['FBA', 'FBM', 'Listing Optimization', 'Account Management'],
    },
    {
      title: 'MS Office Solutions',
      description: 'Professional MS Office solutions for organized, impactful work',
      features: ['Word', 'Excel', 'PowerPoint', 'Data Analysis'],
    },
    {
      title: 'Full Stack Architecture',
      description: 'Designing end-to-end architecture with modern tech stacks',
      features: ['MERN Stack', 'PHP/MySQL', 'Cloud Deployment', 'Security'],
    },
  ],
  projects: [
    {
      title: 'Johnson Graphic Solutions',
      description: 'Professional graphic design agency offering logo design, branding, web development, and social media marketing services.',
      tech: ['HTML5', 'CSS3', 'JavaScript', 'Netlify'],
      demo: 'https://johnson-graphic-solutions.netlify.app/',
      category: 'Client Website',
    },
    {
      title: '1 Sun Screens Services',
      description: 'Premium sun screen solutions for residential and commercial properties. Custom-fit screens, professional installation, and repair services.',
      tech: ['HTML5', 'CSS3', 'JavaScript', 'Netlify'],
      demo: 'http://1-sun-screens-services.netlify.app/',
      category: 'Client Website',
    },
    {
      title: "Romero's Grass Installer",
      description: 'Professional grass installation and landscaping services. Expert turf installation, yard maintenance, and outdoor design solutions.',
      tech: ['HTML5', 'CSS3', 'JavaScript', 'Netlify'],
      demo: 'https://romeros-grass-installer.netlify.app/',
      category: 'Client Website',
    },
    {
      title: 'Injouables',
      description: 'Creative digital solutions and innovative design services. Specializing in modern web experiences and brand identity.',
      tech: ['HTML5', 'CSS3', 'JavaScript', 'Netlify'],
      demo: 'https://injouables.netlify.app/',
      category: 'Client Website',
    },
    {
      title: 'Protex Facility Services LLC',
      description: 'Professional facility management and cleaning services. Comprehensive solutions for commercial and residential properties.',
      tech: ['HTML5', 'CSS3', 'JavaScript', 'Netlify'],
      demo: 'https://protex-facility-services-llc.netlify.app/',
      category: 'Client Website',
    },
    {
      title: 'Elite Crest',
      description: 'Premium design and branding services. Creating distinctive visual identities and marketing solutions for businesses.',
      tech: ['HTML5', 'CSS3', 'JavaScript', 'Netlify'],
      demo: 'https://elite-crest.netlify.app/',
      category: 'Client Website',
    },
    {
      title: 'Injouables 1',
      description: 'Innovative digital design solutions focused on creating engaging online experiences and powerful brand stories.',
      tech: ['HTML5', 'CSS3', 'JavaScript', 'Netlify'],
      demo: 'https://injouables1.netlify.app/',
      category: 'Client Website',
    },
    {
      title: 'No Limit Earthworks LLC',
      description: 'Professional earthworks and excavation services. Land clearing, grading, site preparation, and construction solutions.',
      tech: ['HTML5', 'CSS3', 'JavaScript', 'Netlify'],
      demo: 'https://no-limit-earthworks-llc.netlify.app/',
      category: 'Client Website',
    },
    {
      title: 'Smart Student Productivity System',
      description: 'Advanced productivity system for students. Task management, study planning, progress tracking, and academic tools.',
      tech: ['React.js', 'Node.js', 'MongoDB', 'Express.js', 'JWT'],
      demo: 'https://smart-student-productivity-system.netlify.app/',
      category: 'MERN Application',
    },
    {
      title: 'Memory Remember Matching Game',
      description: 'Interactive memory matching game designed to improve cognitive skills. Fun and engaging brain training experience.',
      tech: ['JavaScript', 'HTML5', 'CSS3'],
      demo: 'https://memory-remember-matching-game.netlify.app/',
      category: 'Game',
    },
    {
      title: 'Nexus 009',
      description: 'Modern digital platform showcasing innovative solutions and creative technologies. Built for performance and user experience.',
      tech: ['Next.js', 'TypeScript', 'Tailwind CSS', 'Framer Motion'],
      demo: 'https://nexus009.netlify.app/',
      category: 'Next.js Application',
    },
    {
      title: 'Nexus Task Manager System',
      description: 'Comprehensive task management system with team collaboration, real-time updates, and project tracking features.',
      tech: ['React.js', 'Node.js', 'PostgreSQL', 'WebSocket', 'Redis'],
      demo: 'https://nexus-task-manager-system.netlify.app/',
      category: 'Full Stack Application',
    },
    {
      title: 'ATS Premium Resume Builder',
      description: 'ATS-friendly resume builder with premium templates. Create professional resumes that pass automated screening systems.',
      tech: ['React.js', 'TypeScript', 'Node.js', 'MongoDB', 'Tailwind CSS'],
      demo: 'https://ats-premium-resume-builder.netlify.app/',
      category: 'MERN Application',
    },
    {
      title: 'AI Professional Email Writer',
      description: 'AI-powered email writing assistant. Generate professional, context-aware emails for business communication.',
      tech: ['React.js', 'Node.js', 'OpenAI API', 'Express.js', 'Tailwind CSS'],
      demo: 'https://ai-professional-email-writer.netlify.app/',
      category: 'AI Application',
    },
    {
      title: 'Fanta Bottle Animated Website',
      description: 'Interactive animated website showcasing Fanta bottle with 3D-like animations and immersive user experience.',
      tech: ['Three.js', 'GSAP', 'JavaScript', 'HTML5', 'CSS3'],
      demo: 'https://fanta-bottle-animated-website.netlify.app/',
      category: 'Animated Website',
    },
  ],
  experiences: [
    {
      title: 'Full Stack Developer',
      company: 'Self-Employed / Freelance',
      period: '2021 - Present',
      description: 'Building scalable web applications using React, Node.js, and PHP/MySQL. Delivering end-to-end solutions with modern technologies.',
    },
    {
      title: 'Amazon Virtual Assistant (V.A)',
      company: 'Remote',
      period: '2022 - 2023',
      description: 'End-to-end Amazon account management specializing in FBA, FBM, and listing optimization. Helped clients grow their Amazon businesses.',
    },
    {
      title: 'MS Office Specialist',
      company: 'Professional Services',
      period: '2020 - 2021',
      description: 'Professional MS Office solutions for accurate, organized, and impactful work. Expertise in Word, Excel, and PowerPoint.',
    },
  ],
  certificates: [
    {
      title: 'Web Development & Design Internship',
      organization: 'HSN Design Studio',
      type: 'Onsite Internship',
      period: 'Jun 20, 2025 to Sept 20, 2025',
      location: 'Onsite',
    },
    {
      title: 'Youth Leadership Program Webinar - Zero to Full Stack Developer Roadmap',
      organization: 'Combine Foundation',
      type: 'Webinar / Online',
      period: '8th March 2026',
      location: 'Online',
    },
    {
      title: 'Full Stack Development Internship',
      organization: 'CodeAlpha',
      type: 'Virtual Internship',
      period: '20th March 2026 to 20th April 2026',
      location: 'Remote',
    },
  ],
  recommendations: [
    {
      name: 'Engr. Muhammad Hassan Memon',
      title: 'CEO, HSN Design Studio (Private) Limited',
      text: 'Sarfraz Ahmed demonstrated remarkable talent, dedication, and a genuine passion for Web Designing & Development during his internship at HSN Design Studio. His exceptional performance and contribution to our team make him a valuable asset to any organization.',
    },
    {
      name: 'Swati Srivastava',
      title: 'Founder & CEO, CodeAlpha',
      text: 'Sarfraz Ahmed has excellent analytical skills and quickly acquired new skills while adeptly adapting to emerging technologies. His dedication, willingness to assist, and strong collaboration with team members make him a valuable asset to any prospective employer.',
    },
  ],
  education: [
    'Web Development & Design Internship - HSN Design Studio (3 months, onsite)',
    'Full Stack Development Internship - CodeAlpha (1 month, remote)',
    'Youth Leadership Program - Combine Foundation (online webinar)',
    'Self-taught via Code With Sheru and hands-on production projects',
  ],
  company: {
    name: 'Code With Sheru',
    about:
      'Code With Sheru is an educational platform founded by Sarfraz Ahmed Sheru. It teaches coding tutorials, web development tips, and modern technologies.',
  },
  contact: {
    location: 'Pakistan',
    phone: '+92 303 3365593',
    phoneRaw: '+923033365593',
    email: 'listenrhymes@gmail.com',
    whatsapp: 'https://wa.me/923033365593',
    workingHours: 'Mon-Fri: 9:00 AM - 11:00 PM',
  },
  socials: {
    github: 'https://github.com/sheru-project',
    linkedin: 'https://www.linkedin.com/in/sarfrazahmed1107/',
    facebook: 'https://www.facebook.com/sherubhai1107',
    whatsapp: 'https://wa.me/923033365593',
    email: 'mailto:listenrhymes@gmail.com',
    call: 'tel:+923033365593',
  },
  resume: 'Sarfraz-Ahmed-Resume.pdf',
  pages: [
    { path: '/', title: 'Home', about: 'Hero, profile photo, stats (3+ years, 25+ projects, 15+ clients), tech stack and core skills.' },
    { path: '/about', title: 'About', about: 'Bio, personal info, achievements, approach, certificates, recommendations, offer letters and work experience.' },
    { path: '/services', title: 'Services', about: 'Frontend, Backend, Database, PHP, UI/UX design, Amazon VA, MS Office and Full Stack architecture services.' },
    { path: '/projects', title: 'Projects', about: '15 client and personal projects including MERN applications, Next.js apps, AI tools, games and client websites.' },
    { path: '/contact', title: 'Contact', about: 'Contact info (location, phone, email, working hours), social links and a contact form.' },
  ],
};

export interface AssistantContext {
  pathname: string;
  theme: ThemeMode;
  language: 'ur' | 'en';
}

const serializeKnowledge = (k: PortfolioKnowledge): string => JSON.stringify(k, null, 2);

/**
 * Builds the OpenAI system prompt from the live portfolio knowledge plus the
 * current app context (page, theme, language).
 */
export function buildSystemPrompt(context: AssistantContext): string {
  const currentPage =
    portfolioKnowledge.pages.find((p) => p.path === context.pathname) ??
    portfolioKnowledge.pages[0];

  return [
    `You are "GeniusMate", Sarfraz Ahmed Sheru Bhai's premium AI personal assistant (like ChatGPT Voice Mode + Siri + Jarvis).`,
    `You control his entire portfolio website. You answer in a warm, human, natural way.`,
    ``,
    `# STRICT RULES`,
    `- NEVER invent facts that are not in the KNOWLEDGE section below. If you are unsure, say you are not sure.`,
    `- Answer ONLY from the provided portfolio knowledge.`,
    `- ALWAYS reply in the same language the user used: Urdu OR English.`,
    `- Keep answers conversational and reasonably short (2-4 sentences usually, unless asked for detail).`,
    `- You can understand context and follow-up questions from the whole conversation history.`,
    `- When the user asks to open a page, link, resume, or change the theme, a real action is executed automatically by the app - just confirm it naturally in your reply.`,
    ``,
    `# CURRENT CONTEXT`,
    `- Current page the visitor is on: ${currentPage.path} (${currentPage.title}).`,
    `- Website theme mode: ${context.theme}.`,
    `- Language: ${context.language === 'ur' ? 'Urdu' : 'English'}.`,
    ``,
    `# KNOWLEDGE`,
    serializeKnowledge(portfolioKnowledge),
    ``,
    `# LEAD GENERATION`,
    `If the visitor expresses interest in hiring or building a website/app/service, run a friendly consultation:`,
    `1) Ask what kind of business or project they have.`,
    `2) Ask their budget range.`,
    `3) Ask their timeline.`,
    `4) Ask what features they need.`,
    `Track which questions are already answered by reading the conversation history - never repeat an answered question.`,
    `Once all four are collected, summarize the requirements and offer these next steps: WhatsApp, Email, and the Contact Form.`,
    ``,
    `# PORTFOLIO CONTROLS`,
    `Navigation, scrolling, external links (GitHub, LinkedIn, WhatsApp, Email, Call, Resume download) and theme switching are handled automatically by the app when the visitor asks. Confirm the action in your reply in a natural, human way.`,
  ].join('\n');
}
